import { AxisLabelsFormatterContextObject, ChartSelectionContextObject, Options, Point } from "highcharts";

export interface ChartCustomization {
  formatPoint(series: string, y: number): string;
  /** Formats y-axis tick labels, e.g. to show absolute values for negated series. */
  formatYAxisLabel?(value: number): string;
  noDataText?: string;
  /** Chart height in px. Defaults to 400. */
  height?: number;
  /** Spacing around the plot area: [top, right, bottom, left]. Defaults to [48, 24, 15, 24]. */
  spacing?: [number, number, number, number];
  /** Fixes the number of y-axis ticks, keeping small charts uncluttered. */
  yTickAmount?: number;
}

export const getChartInfo = (
  self: { selectDate(range: [Date, Date]): void },
  customization: ChartCustomization,
): Options => {
  const formatYAxisLabel = customization.formatYAxisLabel;
  return {
    chart: {
      type: "line",
      zoomType: "x",
      height: customization.height ?? 400,
      showAxes: false,
      backgroundColor: "#FAFBFD",
      spacing: customization.spacing ?? [48, 24, 15, 24],
      selectionMarkerFill: "rgba(0,0,0,0.05)",
      resetZoomButton: {
        position: {
          x: 0,
          y: 8,
        },
      },
      style: {
        fontFamily: "Proxima",
      },
      events: {
        selection(event: ChartSelectionContextObject): boolean | undefined {
          if (event.xAxis !== undefined) {
            /**
             * trigger new data loading after selecting in the chart
             */
            const start = new Date(event.xAxis[0].min);
            const end = new Date(event.xAxis[0].max);
            self.selectDate([start, end]);
            return undefined;
          }
        },
      },
    },
    lang: {
      noData: "Retrieving data...",
    },

    plotOptions: {
      series: {
        states: {
          hover: {
            lineWidthPlus: 0, // disable thicker line on hover
          },
        },
        marker: {
          states: {
            hover: {
              enabled: false, // optional: disable point highlighting
            },
          },
        },
      },
    },

    exporting: {
      chartOptions: {
        chart: {
          style: {
            fontFamily: "Helvetica",
          },
        },
        yAxis: [
          {
            labels: {
              style: {
                font: "9px Helvetica",
              },
            },
            title: {
              text: "",
            },
          },
        ],
        xAxis: [
          {
            type: "datetime",
            dateTimeLabelFormats: {
              hour: "%H:%M",
              minute: "%H:%M",
            },
            labels: {
              style: {
                font: "9px Helvetica",
              },
            },
          },
        ],
      },
      enabled: false,

      csv: {
        decimalPoint: ".",
        lineDelimiter: "\n",
      },
    },

    title: {
      text: undefined,
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      shadow: false,
      backgroundColor: "white",
      borderColor: "#E6E9EB",
      xDateFormat: "%e %b %Y, %H:%M",
      pointFormatter(this: Point): string {
        return customization.formatPoint(this.series.name, this.y ?? 0);
      },
    },
    yAxis: {
      title: {
        text: null,
      },

      gridLineColor: "#E6E9EB",
      tickAmount: customization.yTickAmount,
      labels: {
        style: {
          color: "#7f8c8d",
          font: "9px Proxima",
        },
        formatter: formatYAxisLabel && ((ctx: AxisLabelsFormatterContextObject) => formatYAxisLabel(Number(ctx.value))),
      },
      // minRange: 10000
    },
    xAxis: {
      type: "datetime",
      dateTimeLabelFormats: {
        hour: "%H:%M",
        minute: "%H:%M",
      },
      minPadding: 0,
      maxPadding: 0,
      lineColor: "transparent",
      minorTickLength: 0,
      tickLength: 0,
      labels: {
        style: {
          color: "#7f8c8d",
          font: "9px Proxima",
        },
      },
    },
    credits: {
      enabled: false,
    },
  };
};
